import {
  Circle,
  KeyboardArrowDownOutlined,
  KeyboardArrowRightOutlined,
} from "@mui/icons-material";
import { Box, Tooltip } from "@mui/material";
import { grey } from "@mui/material/colors";
import { AgGridReact } from "ag-grid-react";
import { isEmpty, isNil } from "lodash";
import { useCallback, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { columnTypesBoolean } from "../../../../_src_shared/utils/agGrid/columnTypes/columnTypesBoolean";
import { columnTypesDate } from "../../../../_src_shared/utils/agGrid/columnTypes/columnTypesDate";
import { sidebarSetValues } from "../../../../redux/actions/sidebarActions";
import { filePreviewSetData } from "../../../../redux/reducers/filePreviewReducer";
import { formatDateShort } from "../../../../utils/dates/formatDateShort";
import AgGridTableWrapper from "../../../agGrid/AgGridTableWrapper";
import ObjectInfoTooltip from "../../../data/ObjectInfoTooltip";
import Flx from "../../../layout/Flx";
import Txt from "../../../typography/Txt";
import LoanFileCommentsBadge from "../LoanFileCommentsBadge";

const CategorizedLoanDocumentsTable = ({
  quickFilter,
  rowData,
  groupDocGroups = false,
  groupByDocType = false, // New prop to control docType grouping
  height,
  docGroups,
  sx,
  domLayout = "autoHeight",
}) => {
  const ref = useRef();
  const dispatch = useDispatch();
  const sidebarType = useSelector((state) => state.sidebar?.type);

  const filteredRowData = useMemo(() => {
    if (isNil(docGroups)) {
      return rowData;
    }
    return rowData.filter((doc) => docGroups.includes(doc.docGroup));
  }, [rowData, docGroups]);

  /**
   * COLUMNS
   */
  const columnTypes = useMemo(() => {
    return {
      ...columnTypesBoolean(),
      ...columnTypesDate(),
    };
  }, []);

  const columnDefs = useMemo(
    () => [
      {
        field: "docGroup",
        rowGroup: groupDocGroups,
        hide: true,
        // ensure A→Z for group names if grouping by group
        sort: groupDocGroups ? "asc" : undefined,
        sortIndex: groupDocGroups ? 0 : undefined,
        comparator: (a, b) => {
          const av = a || "Uncategorized";
          const bv = b || "Uncategorized";
          return av.toLowerCase().localeCompare(bv.toLowerCase());
        },
        groupComparator: (a, b) => {
          const av = a || "Uncategorized";
          const bv = b || "Uncategorized";
          return av.toLowerCase().localeCompare(bv.toLowerCase());
        },
      },
      {
        field: "docType",
        rowGroup: groupByDocType,
        hide: true,
        // 🔑 this actually turns on the sort so your comparator runs
        sort: groupByDocType ? "asc" : undefined,
        // if both groupings are enabled, sort docType after docGroup
        sortIndex: groupByDocType ? (groupDocGroups ? 1 : 0) : undefined,
        comparator: (valueA, valueB) => {
          const a = valueA || "Uncategorized";
          const b = valueB || "Uncategorized";
          return a.toLowerCase().localeCompare(b.toLowerCase());
        },
        groupComparator: (valueA, valueB) => {
          const a = valueA || "Uncategorized";
          const b = valueB || "Uncategorized";
          return a.toLowerCase().localeCompare(b.toLowerCase());
        },
      },
      {
        field: "file_display_name",
        flex: 1,
        minWidth: 250,
        autoHeight: true,
        cellRenderer: (params) => {
          return (
            <Flx fw jb ac sx={{ py: 1, px: 0.7, pl: 2.5 }}>
              <Flx column gap={0.3} sx={{ minWidth: 0, flex: 1 }}>
                <Txt
                  fontSize={"12px"}
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={params.value}
                >
                  {params.value}
                </Txt>
                <Flx ac wrap g={0.8}>
                  {!groupByDocType && (
                    <>
                      <Txt sx={{ fontSize: "10px" }} secondary>
                        {params?.data?.docType || "Uncategorized"}
                      </Txt>
                      <Circle sx={{ width: 4, height: 4, color: grey[700] }} />
                    </>
                  )}
                  <Tooltip title="Upload Date">
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.3 }}
                    >
                      <Txt sx={{ fontSize: "10px" }} secondary>
                        {formatDateShort(params?.data?.uploadDate)}
                      </Txt>
                    </Box>
                  </Tooltip>
                  <Circle sx={{ width: 4, height: 4, color: grey[700] }} />
                  <LoanFileCommentsBadge
                    showZero
                    file={params.data}
                    countButton
                    sx={{ color: "rgba(0, 0, 0, 0.6)" }}
                  />
                </Flx>
              </Flx>
            </Flx>
          );
        },
      },
      {
        flex: 0,
        maxWidth: 60,
        cellStyle: {
          justifyContent: "center",
          alignItems: "center",
          display: "flex",
        },
        cellRenderer: (params) => {
          return (
            <ObjectInfoTooltip
              title="File Details"
              format={[
                { field: "modifiedDate", type: "date" },
                { field: "uploadDate", type: "date" },
                { field: "reportEffectiveDate", type: "date" },
                { field: "uploadDate", type: "date" },
              ]}
              data={params?.data}
              excludeKeys={[
                "_id",
                "content_type",
                "container_type",
                "dealId",
                "file_storage_name",
                "containerName",
              ]}
            />
          );
        },
      },
    ],
    [groupDocGroups, groupByDocType]
  );

  const defaultColDef = useMemo(
    () => ({
      filter: true,
      enableRowGroup: groupDocGroups || groupByDocType,
      suppressHeaderFilterButton: true,
      suppressHeaderMenuButton: true,
      minWidth: 200,
    }),
    [groupDocGroups, groupByDocType]
  );

  /**
   * Auto group column definition for custom group rendering
   */
  const autoGroupColumnDef = useMemo(
    () => ({
      headerName: "Groups",
      minWidth: 300,
      cellRendererParams: {
        suppressCount: false,
      },
    }),
    []
  );

  /**
   * ROWS
   */
  const getRowStyle = useCallback((params) => {
    if (params.node.group) {
      return { fontWeight: 700 };
    }
    return null;
  }, []);

  const onRowClicked = useCallback(
    (params) => {
      if (params.node.group) {
        params.node.setExpanded(!params.node.expanded);
        return;
      }
      const file = params?.data;
      if (isEmpty(file)) {
        return;
      }
      if (sidebarType === "uwChecklist") {
        dispatch(filePreviewSetData(file));
        return;
      }

      dispatch(
        sidebarSetValues({
          type: "filePreview",
          state: file,
        })
      );
    },
    [dispatch, sidebarType]
  );

  /**
   * Determine the default group expansion level
   */
  const groupDefaultExpanded = useMemo(() => {
    if (groupDocGroups && groupByDocType) {
      return 1; // Expand first level (docGroup) by default when both are active
    }
    return 1; // Expand first level by default
  }, [groupDocGroups, groupByDocType]);

  if (isEmpty(rowData)) {
    return (
      <Box sx={{ p: 2 }}>
        <Txt secondary>No files uploaded</Txt>
      </Box>
    );
  }

  return (
    <AgGridTableWrapper
      simpleTable
      sx={sx}
      height={height}
      suppressMinHeightWhenPopulated={rowData}
    >
      <AgGridReact
        ref={ref}
        rowData={filteredRowData}
        columnDefs={columnDefs}
        quickFilterText={quickFilter}
        defaultColDef={defaultColDef}
        columnTypes={columnTypes}
        headerHeight={0}
        getRowStyle={getRowStyle}
        onRowClicked={onRowClicked}
        groupRowRenderer={GroupRowRenderer}
        autoGroupColumnDef={autoGroupColumnDef}
        groupDisplayType={"groupRows"}
        groupDefaultExpanded={groupDefaultExpanded}
        domLayout={domLayout}
        enableCellTextSelection
      />
    </AgGridTableWrapper>
  );
};

const GroupRowRenderer = (params) => {
  const expanded = params.node.expanded;

  const getGroupLabel = () => {
    const value =
      isNil(params.value) || params.value === ""
        ? "Uncategorized"
        : params.value;
    return value;
  };

  return (
    <Flx
      ac
      gap={0.5}
      sx={{
        height: "100%",
        position: "absolute",
        left: 0,
        top: 0,
        width: "100%",
        pl: params.node.level * 2 + 1.5,
      }}
    >
      {expanded ? (
        <KeyboardArrowDownOutlined className="thin" />
      ) : (
        <KeyboardArrowRightOutlined className="thin" />
      )}
      <Box
        component="span"
        sx={{
          fontSize: "12px",
          fontWeight: 600,
        }}
      >
        {getGroupLabel()} ({params.node.allChildrenCount})
      </Box>
    </Flx>
  );
};

export default CategorizedLoanDocumentsTable;
